import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Save, Trash2, Rocket, Eye } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import { Skeleton } from "@/pages/admin/AdminPages";
import { DiffView } from "@/pages/admin/AdminKnowledge";
import api, { apiErrorMessage } from "@/lib/api";

const inp = "w-full rounded-lg border border-hborder bg-white px-3 py-2 text-sm outline-none focus:border-tblue";

const toForm = (r) => ({
  code: r.code, title: r.title, description: r.description || "", required: r.required !== false,
  certainty: r.certainty || "official", category: r.category || "General",
  evidence: (r.evidence_types || []).join(", "),
  conditions: r.applicant_conditions ? JSON.stringify(r.applicant_conditions) : "",
  period: r.required_period_months ?? "", srcName: r.source?.name || "", srcUrl: r.source?.url || "",
});

function toPayload(f) {
  let cond = null;
  if (f.conditions.trim()) {
    try { cond = JSON.parse(f.conditions); } catch { throw new Error(`“${f.code || f.title}”: applicant conditions must be valid JSON, e.g. {"employment_status": ["employed"]}`); }
    if (typeof cond !== "object" || Array.isArray(cond)) throw new Error(`“${f.code}”: conditions must be a JSON object`);
  }
  return {
    code: f.code.trim(), title: f.title, description: f.description, required: f.required,
    certainty: f.certainty, category: f.category,
    evidence_types: f.evidence.split(",").map((x) => x.trim()).filter(Boolean),
    applicant_conditions: cond, required_period_months: f.period === "" ? null : Number(f.period),
    source: f.srcName || f.srcUrl ? { name: f.srcName, url: f.srcUrl } : null,
  };
}

export default function AdminDraft() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [meta, setMeta] = useState({ origin_scope: "", visa_fee: "", effective_date: "" });
  const [dirty, setDirty] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [conds, setConds] = useState({ employment_status: "", is_minor: false, previous_refusal: false, sponsored: false });
  const [preview, setPreview] = useState(null);
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    api.get(`/admin/knowledge/sets/${id}`).then(({ data }) => {
      setD(data);
      setReqs(data.requirements.map(toForm));
      setMeta({ origin_scope: data.set.origin_scope || "", visa_fee: data.set.visa_fee || "", effective_date: data.set.effective_date || "" });
    }).catch(() => setD(false));
  }, [id]);

  const setReq = (i, patch) => { setReqs((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r))); setDirty(true); };

  const save = async (silent) => {
    try {
      await api.put(`/admin/knowledge/drafts/${id}`, { requirements: reqs.map(toPayload), ...meta });
      setDirty(false);
      if (!silent) toast.success("Draft saved");
      return true;
    } catch (e) {
      toast.error(e.response ? apiErrorMessage(e.response.data?.detail) : e.message);
      return false;
    }
  };

  const runPreview = async () => {
    if (!(await save(true))) return;
    const conditions = {};
    if (conds.employment_status) conditions.employment_status = conds.employment_status;
    ["is_minor", "previous_refusal", "sponsored"].forEach((k) => { if (conds[k]) conditions[k] = true; });
    setPreview((await api.post(`/admin/knowledge/drafts/${id}/preview`, { conditions })).data);
    if (d.set.based_on_set_id) {
      setDiff((await api.get("/admin/knowledge/diff", { params: { a: d.set.based_on_set_id, b: id } })).data);
    }
  };

  const publish = async () => {
    if (!(await save(true))) return;
    try {
      const { data } = await api.post(`/admin/knowledge/drafts/${id}/publish`);
      toast.success(`Published v${data.version}. Existing cases keep their pinned version.`);
      navigate(`/admin/knowledge/sets/${id}`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); setConfirmPublish(false); }
  };

  const discard = async () => {
    if (!window.confirm("Discard this draft? This cannot be undone.")) return;
    await api.delete(`/admin/knowledge/drafts/${id}`);
    toast.success("Draft discarded");
    navigate("/admin/knowledge");
  };

  if (d === false || (d && d.set.status !== "draft")) {
    return <AdminShell title="Draft editor"><p className="text-sm text-muted" data-testid="draft-not-editable">This set is not an editable draft. <Link to={`/admin/knowledge/sets/${id}`} className="text-tblue underline">View it</Link>.</p></AdminShell>;
  }

  return (
    <AdminShell title={d ? `Draft v${d.set.version} · ${d.destination}` : "Draft editor"} subtitle={d ? d.category_name : ""}
      actions={d && (
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/knowledge" className="rounded-lg border border-hborder bg-white px-3 py-2 text-sm">← Back</Link>
          <button onClick={() => save()} data-testid="draft-save" className="flex items-center gap-1.5 rounded-lg border border-tblue px-3 py-2 text-sm font-medium text-tblue"><Save size={15} /> Save{dirty ? " *" : ""}</button>
          {confirmPublish
            ? <button onClick={publish} data-testid="draft-publish-confirm" className="flex items-center gap-1.5 rounded-lg bg-coral px-3 py-2 text-sm font-medium text-white"><Rocket size={15} /> Confirm publish</button>
            : <button onClick={() => setConfirmPublish(true)} data-testid="draft-publish" className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-sm font-medium text-white"><Rocket size={15} /> Publish</button>}
          <button onClick={discard} data-testid="draft-discard" className="rounded-lg border border-coral/40 px-3 py-2 text-coral" aria-label="Discard draft"><Trash2 size={15} /></button>
        </div>
      )}>
      {!d ? <Skeleton /> : (
        <div className="space-y-6">
          {confirmPublish && <p className="rounded-lg border border-coral/30 bg-white p-3 text-sm text-coral" data-testid="draft-publish-warning">Publishing supersedes the current active version for new cases. Existing cases keep their pinned version. Click “Confirm publish” to proceed.</p>}
          <section className="grid gap-3 rounded-md border border-hborder bg-white p-5 md:grid-cols-3">
            <label className="text-xs text-muted">Origin scope<input className={inp} value={meta.origin_scope} onChange={(e) => { setMeta({ ...meta, origin_scope: e.target.value }); setDirty(true); }} data-testid="draft-scope" /></label>
            <label className="text-xs text-muted">Visa fee<input className={inp} value={meta.visa_fee} onChange={(e) => { setMeta({ ...meta, visa_fee: e.target.value }); setDirty(true); }} data-testid="draft-fee" /></label>
            <label className="text-xs text-muted">Effective date<input type="date" className={inp} value={meta.effective_date} onChange={(e) => { setMeta({ ...meta, effective_date: e.target.value }); setDirty(true); }} data-testid="draft-effective" /></label>
          </section>

          <div className="space-y-4" data-testid="draft-requirements">
            {reqs.map((r, i) => (
              <div key={i} className="rounded-md border border-hborder bg-white p-5" data-testid={`draft-req-${i}`}>
                <div className="grid gap-3 md:grid-cols-4">
                  <label className="text-xs text-muted">Code<input className={inp} value={r.code} onChange={(e) => setReq(i, { code: e.target.value })} data-testid={`draft-code-${i}`} /></label>
                  <label className="text-xs text-muted md:col-span-2">Title<input className={inp} value={r.title} onChange={(e) => setReq(i, { title: e.target.value })} data-testid={`draft-title-${i}`} /></label>
                  <label className="text-xs text-muted">Category<input className={inp} value={r.category} onChange={(e) => setReq(i, { category: e.target.value })} /></label>
                  <label className="text-xs text-muted md:col-span-4">Description<textarea rows={2} className={inp} value={r.description} onChange={(e) => setReq(i, { description: e.target.value })} data-testid={`draft-desc-${i}`} /></label>
                  <label className="text-xs text-muted">Certainty
                    <select className={inp} value={r.certainty} onChange={(e) => setReq(i, { certainty: e.target.value })}><option value="official">official</option><option value="guidance">guidance</option></select></label>
                  <label className="flex items-end gap-2 pb-2 text-sm text-heading"><input type="checkbox" checked={r.required} onChange={(e) => setReq(i, { required: e.target.checked })} /> Required</label>
                  <label className="text-xs text-muted">Evidence types (comma separated)<input className={inp} value={r.evidence} onChange={(e) => setReq(i, { evidence: e.target.value })} /></label>
                  <label className="text-xs text-muted">Required period (months)<input type="number" className={inp} value={r.period} onChange={(e) => setReq(i, { period: e.target.value })} /></label>
                  <label className="text-xs text-muted md:col-span-2">Applies when (JSON, empty = everyone)<input className={`${inp} font-mono`} value={r.conditions} placeholder='{"employment_status": ["employed"]}' onChange={(e) => setReq(i, { conditions: e.target.value })} data-testid={`draft-cond-${i}`} /></label>
                  <label className="text-xs text-muted">Source name<input className={inp} value={r.srcName} onChange={(e) => setReq(i, { srcName: e.target.value })} /></label>
                  <label className="text-xs text-muted">Source URL<input className={inp} value={r.srcUrl} onChange={(e) => setReq(i, { srcUrl: e.target.value })} /></label>
                </div>
                <button onClick={() => { setReqs(reqs.filter((_, j) => j !== i)); setDirty(true); }} data-testid={`draft-remove-${i}`} className="mt-3 flex items-center gap-1 text-xs text-coral"><Trash2 size={13} /> Remove requirement</button>
              </div>
            ))}
            <button onClick={() => { setReqs([...reqs, toForm({ code: "", title: "", certainty: "official", required: true })]); setDirty(true); }} data-testid="draft-add" className="flex items-center gap-1.5 rounded-lg border border-dashed border-tblue px-4 py-2.5 text-sm text-tblue"><Plus size={15} /> Add requirement</button>
          </div>

          <section className="rounded-md border border-hborder bg-white p-5" data-testid="draft-preview-panel">
            <h2 className="font-serif text-lg text-heading">Preview against sample applicant</h2>
            <div className="mt-3 flex flex-wrap items-end gap-4 text-sm">
              <label className="text-xs text-muted">Employment status
                <select className={inp} value={conds.employment_status} onChange={(e) => setConds({ ...conds, employment_status: e.target.value })} data-testid="preview-employment">
                  <option value="">(not set)</option>{["employed", "self_employed", "student", "unemployed", "retired"].map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
              {["is_minor", "previous_refusal", "sponsored"].map((k) => (
                <label key={k} className="flex items-center gap-2 pb-2 text-heading"><input type="checkbox" checked={conds[k]} onChange={(e) => setConds({ ...conds, [k]: e.target.checked })} /> {k.replace(/_/g, " ")}</label>
              ))}
              <button onClick={runPreview} data-testid="draft-preview-run" className="flex items-center gap-1.5 rounded-lg bg-tblue px-4 py-2 text-sm font-medium text-white"><Eye size={15} /> Save & preview</button>
            </div>
            {preview && (
              <div className="mt-4" data-testid="draft-preview-result">
                <p className="text-sm text-muted">{preview.applicable.length} of {preview.total} requirements apply:</p>
                <ul className="mt-2 grid gap-1.5 md:grid-cols-2">
                  {preview.applicable.map((a) => <li key={a.code} className="rounded-lg bg-softcream px-3 py-2 text-sm"><b className="text-heading">{a.title}</b> <span className="text-xs text-muted">{a.req_status}{a.conditional ? " · conditional" : ""}</span></li>)}
                </ul>
              </div>
            )}
            {diff && <div className="mt-5"><h3 className="mb-2 text-sm font-medium text-heading">Changes vs. base version</h3><DiffView diff={diff} /></div>}
          </section>
        </div>
      )}
    </AdminShell>
  );
}
